import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { useBlocker, useParams } from 'react-router';

import { ConfirmModal } from 'components/modal';
import { useFormContext } from 'context/FormContext';
import { LojaColunasDinamicasForm, LojaDadosBasicosForm } from 'pages/cadastro/loja/components';
import { useQueryGrupoExibicao } from 'pages/cadastro/secoes/hooks';

export type LojaStep = {
    idConfiguracaoPropriedadeGrupoExibicao: number;
    dsConfiguracaoPropriedadeGrupoExibicao: string;
    fgDirty: boolean;
    fgDisabled: boolean;
    component: ReactNode;
};

type LojaStepContextData = {
    activeStep: LojaStep;
    stepList: LojaStep[];
    setActiveStepDirty(dirty?: boolean): void;
    requestStepChange(step: LojaStep): void;
};

const DADOS_BASICOS_STEP: LojaStep = {
    idConfiguracaoPropriedadeGrupoExibicao: 0,
    dsConfiguracaoPropriedadeGrupoExibicao: 'Dados Básicos',
    fgDirty: false,
    fgDisabled: false,
    component: <LojaDadosBasicosForm />,
};

const LojaStepContext = createContext<LojaStepContextData>({} as LojaStepContextData);

export function LojaStepProvider({ children }: { children: ReactNode }) {
    const { id } = useParams();
    const [activeStep, setActiveStep] = useState({ ...DADOS_BASICOS_STEP, fgDirty: !id });
    const [stepList, setStepList] = useState<LojaStep[]>([{ ...DADOS_BASICOS_STEP, fgDirty: !id }]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const pendingStep = useRef<LojaStep | null>(null);
    const { setContent } = useFormContext();
    const { data } = useQueryGrupoExibicao();
    const { state, proceed, reset } = useBlocker(activeStep.fgDirty);

    /**
     * Atualiza as steps que já estão salvas.
     */
    const updateInitialSteps = () => {
        if (data) {
            const list = data.data
                .filter((item) => item.fgAtivo)
                .map((item) => ({
                    idConfiguracaoPropriedadeGrupoExibicao: item.idConfiguracaoPropriedadeGrupoExibicao!,
                    dsConfiguracaoPropriedadeGrupoExibicao: item.dsConfiguracaoPropriedadeGrupoExibicao,
                    fgDirty: !id,
                    fgDisabled: !id,
                    component: <LojaColunasDinamicasForm />,
                }));

            setStepList([{ ...DADOS_BASICOS_STEP, fgDirty: !id }, ...list]);
        }
    };

    /**
     * Habilita todas as steps caso tenha id.
     */
    const updateDisabledSteps = () => {
        if (id) setStepList((list) => list.map((step) => ({ ...step, fgDisabled: false })));
    };

    /**
     * Suja o step atual.
     */
    const setActiveStepDirty = (fgDirty = true) => {
        setStepList((list) =>
            list.map((step) => {
                if (step.idConfiguracaoPropriedadeGrupoExibicao === activeStep.idConfiguracaoPropriedadeGrupoExibicao) return { ...step, fgDirty };

                return step;
            }),
        );

        setActiveStep((prevActiveStep) => ({ ...prevActiveStep, fgDirty }));
    };

    /**
     * Solicita a mudança de step.
     *
     * Caso o step atual esteja sujo, exibe um modal de confirmação.
     */
    const requestStepChange = (step: LojaStep) => {
        if (activeStep.idConfiguracaoPropriedadeGrupoExibicao === step.idConfiguracaoPropriedadeGrupoExibicao) return;

        if (activeStep.fgDirty) {
            pendingStep.current = step;

            setShowConfirmModal(true);
        } else {
            setActiveStep(step);
        }
    };

    /**
     * Confirma a mudança de step.
     *
     * Se o usuário está saindo da tela, prossegue com a mudança.
     *
     * Caso contrário, limpa o conteúdo do contexto de form, define o step atual como salvo e sujo e define o step pendente como ativo.
     */
    const confirmStepChange = () => {
        if (state === 'blocked') {
            proceed();

            return;
        }

        setContent({});

        setActiveStepDirty(false);

        if (pendingStep.current) {
            setActiveStep(pendingStep.current);

            pendingStep.current = null;
        }
    };

    useEffect(updateInitialSteps, [data]);

    useEffect(updateDisabledSteps, [id]);

    return (
        <LojaStepContext.Provider
            value={{
                activeStep,
                stepList,
                setActiveStepDirty,
                requestStepChange,
            }}
        >
            <ConfirmModal
                open={showConfirmModal || state === 'blocked'}
                setOpen={setShowConfirmModal}
                text="Ao sair ou trocar de configuração, alterações não salvas serão perdidas. Gostaria de continuar?"
                confirm={confirmStepChange}
                cancel={() => {
                    if (state === 'blocked') {
                        reset();
                    }
                }}
            />

            {children}
        </LojaStepContext.Provider>
    );
}

export const useLojaStepContext = () => useContext(LojaStepContext);
